import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';
import './ExameDetalhes.css';

const styles = {
  page: {
    padding: '24px',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    fontFamily: 'Inter, Roboto, "Open Sans", sans-serif',
  },
  badgeAtivo: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '16px',
    fontSize: '0.85rem',
    fontWeight: '600',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  badgeBloqueado: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '16px',
    fontSize: '0.85rem',
    fontWeight: '600',
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  sectionCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: '700',
    color: '#333',
    margin: '0 0 16px 0',
    paddingBottom: '8px',
    borderBottom: '2px solid #1976d2',
    display: 'inline-block',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailLabel: {
    fontSize: '0.75rem',
    color: '#999',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  detailValue: {
    fontSize: '0.9rem',
    color: '#333',
    fontWeight: '500',
  },
  textBlock: {
    fontSize: '0.9rem',
    color: '#333',
    backgroundColor: '#fafafa',
    padding: '16px',
    borderRadius: '6px',
    border: '1px solid #eee',
    margin: 0,
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
    fontSize: '1rem',
  },
  errorState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#c62828',
    fontSize: '1rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.85rem',
  },
  th: {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#666',
    backgroundColor: '#fafafa',
    borderBottom: '2px solid #eee',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
  },
  badgeTipo: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '10px',
    fontSize: '0.75rem',
    fontWeight: '600',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
  },
};

function LaudoDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [laudo, setLaudo] = useState(null);
  const [exames, setExames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLaudo();
  }, [id]);

  const fetchLaudo = async () => {
    setLoading(true);
    setError('');
    try {
      const [laudoResp, examesResp] = await Promise.all([
        axios.get(`${API_URL}/laudos/${id}`),
        axios.get(`${API_URL}/laudos/${id}/exames`).catch(() => ({ data: [] })),
      ]);
      setLaudo(laudoResp.data);
      setExames(examesResp.data || []);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Laudo nao encontrado.' : 'Erro ao carregar laudo: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={styles.loadingState}>Carregando detalhes do laudo...</div>;
  }

  if (error) {
    return (
      <div className="data-page" style={styles.page}>
        <button className="back-button" onClick={() => navigate('/laudos')}>← Voltar</button>
        <div style={styles.errorState}>{error}</div>
      </div>
    );
  }

  if (!laudo) return null;

  return (
    <div className="data-page" style={styles.page}>
      {/* Header */}
      <div className="page-header-detalhes">
        <button onClick={() => navigate('/laudos')} className="back-button">← Voltar</button>
        <h1>{laudo.DESCRICAO || laudo.ID}</h1>
        <span style={laudo.BLOQUEADO === 'T' ? styles.badgeBloqueado : styles.badgeAtivo}>
          {laudo.BLOQUEADO === 'T' ? 'Bloqueado' : 'Ativo'}
        </span>
        <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
          Codigo: <strong>{laudo.ID}</strong>
          {laudo.SINONIMO && <span> — Sinonimo: {laudo.SINONIMO}</span>}
        </p>
      </div>

      {/* Dados Gerais */}
      <div style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>Dados Gerais</h3>
        <div className="responsive-grid" style={styles.detailGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Codigo</span>
            <span style={{ ...styles.detailValue, fontFamily: 'monospace' }}>{laudo.ID}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Descricao</span>
            <span style={styles.detailValue}>{laudo.DESCRICAO || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Sinonimo</span>
            <span style={styles.detailValue}>{laudo.SINONIMO || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Material</span>
            <span style={styles.detailValue}>{laudo.MATERIAL_NOME || laudo.MATERIAL || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Metodo</span>
            <span style={styles.detailValue}>{laudo.METODO_NOME || laudo.METODO || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Sexo</span>
            <span style={styles.detailValue}>
              {laudo.SEXO === 'M' ? 'Masculino' : laudo.SEXO === 'F' ? 'Feminino' : laudo.SEXO || 'Ambos'}
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Dias para Entrega</span>
            <span style={styles.detailValue}>{laudo.DIASENTREGA || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Codigo TUSS</span>
            <span style={{ ...styles.detailValue, fontFamily: 'monospace' }}>{laudo.CODIGO_TUSS || '-'}</span>
          </div>
        </div>
      </div>

      {/* Preparo */}
      <div style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>Preparo do Paciente</h3>
        {laudo.PREPARO ? (
          <pre style={styles.textBlock}>{laudo.PREPARO}</pre>
        ) : (
          <p style={{ fontSize: '0.9rem', color: '#999', margin: 0 }}>Nenhum preparo definido para este laudo.</p>
        )}
      </div>

      {/* Observacao Padrao */}
      {laudo.OBSPADRAO && (
        <div style={styles.sectionCard}>
          <h3 style={styles.sectionTitle}>Observacao Padrao</h3>
          <pre style={styles.textBlock}>{laudo.OBSPADRAO}</pre>
        </div>
      )}

      {/* Exames / Componentes */}
      <div style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>Componentes / Exames ({exames.length})</h3>
        {exames.length === 0 ? (
          <p style={{ fontSize: '0.9rem', color: '#999', margin: 0 }}>Nenhum componente vinculado a este laudo.</p>
        ) : (
          <div style={{ borderRadius: '6px', border: '1px solid #eee', overflow: 'hidden', overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '60px' }}>ID</th>
                  <th style={{ ...styles.th, width: '100px' }}>Sigla</th>
                  <th style={{ ...styles.th, width: '120px' }}>Unidade</th>
                  <th style={{ ...styles.th, width: '110px' }}>Valor Padrao</th>
                  <th style={{ ...styles.th, width: '90px', textAlign: 'center' }}>Tipo</th>
                  <th style={{ ...styles.th, width: '70px', textAlign: 'center' }}>Obrig.</th>
                </tr>
              </thead>
              <tbody>
                {exames.map((exame, idx) => (
                  <tr
                    key={exame.ID}
                    style={{
                      ...(idx % 2 === 0 ? {} : { backgroundColor: '#fafafa' }),
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/exames/${exame.ID}`)}
                  >
                    <td style={{ ...styles.td, fontWeight: '600', color: '#999', fontSize: '0.8rem' }}>{exame.ID}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.8rem' }}>{exame.SIGLA || '-'}</td>
                    <td style={{ ...styles.td, fontSize: '0.8rem' }}>{exame.UNIDADE || '-'}</td>
                    <td style={{ ...styles.td, fontSize: '0.8rem' }}>{exame.VALORPADRAO || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {exame.TIPO ? <span style={styles.badgeTipo}>{exame.TIPO}</span> : '-'}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center', fontSize: '0.8rem' }}>
                      {exame.OBRIGATORIO === 'T' ? 'Sim' : exame.OBRIGATORIO === 'F' ? 'Nao' : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default LaudoDetalhes;
